import { useEffect, useState } from 'react';

/**
 * Returns true once the window (or any of its overflow-scroll descendants)
 * has scrolled past the given threshold. Used to toggle sticky-header borders
 * on only after the user scrolls.
 *
 * Performance notes (Feb 2026):
 *   The previous implementation ran `document.querySelectorAll('.overflow-y-auto')`
 *   on EVERY scroll event — on mobile this walks the whole DOM (which on
 *   the War Room means 150+ golfer nodes) synchronously, freezing the main
 *   thread for multi-second periods during fast flings. Two fixes here:
 *
 *   1. Throttle via `requestAnimationFrame` so we run at most once per
 *      paint frame regardless of how aggressive the scroll velocity is.
 *   2. Cache the overflow-y-auto containers on mount and refresh ONLY on
 *      window resize. The shell layouts don't add/remove these containers
 *      mid-scroll, so caching is safe and saves a full DOM walk every event.
 */
export function useScrolled(threshold = 4) {
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    let containers = Array.from(document.querySelectorAll('.overflow-y-auto'));
    let ticking = false;
    let lastValue = false;

    const compute = () => {
      ticking = false;
      let next = false;
      if (window.scrollY > threshold) {
        next = true;
      } else {
        for (const c of containers) {
          if (c.scrollTop > threshold) { next = true; break; }
        }
      }
      if (next !== lastValue) {
        lastValue = next;
        setScrolled(next);
      }
    };

    const onScroll = () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(compute);
    };

    const refreshContainers = () => {
      containers = Array.from(document.querySelectorAll('.overflow-y-auto'));
    };

    compute();
    window.addEventListener('scroll', onScroll, { passive: true });
    containers.forEach(c => c.addEventListener('scroll', onScroll, { passive: true }));
    window.addEventListener('resize', refreshContainers, { passive: true });

    return () => {
      window.removeEventListener('scroll', onScroll);
      containers.forEach(c => c.removeEventListener('scroll', onScroll));
      window.removeEventListener('resize', refreshContainers);
    };
  }, [threshold]);

  return scrolled;
}
